import type { User } from 'common/src/utils/types';
import type { SidebarItem } from '../components/layouts/sidebars/SidebarTypes';

/**
 * Novedades de la plataforma: se muestran una vez, en un modal, a los usuarios
 * que ya usaban la plataforma cuando salió la novedad.
 *
 * Para agregar una: un objeto más en ANNOUNCEMENTS (las más nuevas al final).
 * - `launchedAt`: cuándo llegó a prod. Solo la ven usuarios creados antes, y
 *   no se muestra antes de esa fecha (se puede mergear antes del lanzamiento).
 * - `requires`: keys de ítems del menú lateral que el usuario tiene que ver
 *   (ver visibleSidebarKeys). El menú resuelve flags de la exportadora y
 *   listas de exportadoras; los permisos de usuarios basic se revisan aquí.
 * - `steps`: pantallas del modal; `image` es opcional (ruta en /public).
 */

type Copy = { es: string; en: string };

export type AnnouncementStep = {
  title: Copy;
  body: Copy;
  image?: string;
};

export type Announcement = {
  id: string;
  launchedAt: string;
  requires: string[];
  route: string;
  cta: Copy;
  steps: [AnnouncementStep, ...AnnouncementStep[]];
};

export const ANNOUNCEMENTS: Announcement[] = [
  {
    id: 'commercial-program',
    // Release #1077 a prod.
    launchedAt: '2026-10-13T09:45:54Z',
    requires: ['commercial-plan'],
    // Gestionar compromisos: ahí se carga o se importa el programa.
    route: '/commercial-plan/detail',
    cta: { es: 'Cargar mi programa', en: 'Load my program' },
    steps: [
      {
        title: {
          es: 'Presentamos Programa Comercial',
          en: 'Introducing Commercial Program',
        },
        body: {
          es: 'Carga tu programa por cliente y semana, y la plataforma lo compara sola con lo que despachas. En cajas equivalentes, kilos o embarques.',
          en: 'Load your program by client and week, and the platform compares it with what you ship. In equivalent boxes, kilos or shipments.',
        },
        image: '/announcements/programa-comercial-1.webp',
      },
      {
        title: {
          es: 'Ve cómo vas con cada cliente',
          en: 'See how you are doing with each client',
        },
        body: {
          es: 'Cada celda muestra despachado / comprometido: verde si cumpliste, amarillo si fue parcial y rojo si no salió nada. Guarda versiones y deja notas por cliente o por semana.',
          en: 'Each cell shows shipped / committed: green if you delivered, yellow if partial and red if nothing went out. Save versions and leave notes by client or by week.',
        },
        image: '/announcements/programa-comercial-2.webp',
      },
      {
        title: {
          es: 'Cárgalo como te acomode',
          en: 'Load it however you like',
        },
        body: {
          es: 'Puedes irlo completando directo en la grilla, semana a semana, o subirlo de una vez desde Excel con nuestra plantilla. Muy pronto también vas a poder descargar reportes de tu programa para compartirlos con tus clientes.',
          en: 'Fill it in right on the grid, week by week, or upload it all at once from Excel with our template. Soon you will also be able to download reports of your program to share with your clients.',
        },
        image: '/announcements/programa-comercial-3.webp',
      },
    ],
  },
];

/**
 * Keys de los ítems del menú que el usuario puede abrir. Un usuario basic solo
 * los que tiene habilitados: el menú no filtra cuando no tiene ningún permiso
 * cargado, así que aquí no se confía en eso.
 */
export const visibleSidebarKeys = (
  items: SidebarItem[],
  profile: User
): Set<string> => {
  const isBasic = profile.roles?.includes('basic');
  const enabled = new Set(
    (profile.permissions ?? [])
      .filter((permission) => permission.enabled)
      .map((permission) => permission.sidebarItemKey)
  );
  const keys = new Set<string>();
  const visit = (item: SidebarItem): boolean => {
    const visible = item.items?.length
      ? item.items.map(visit).some(Boolean)
      : !isBasic || enabled.has(item.permissionKey || item.key);
    if (visible) keys.add(item.key);
    return visible;
  };
  items.forEach(visit);
  return keys;
};

/**
 * Novedades que le faltan ver al usuario, de la más antigua a la más nueva.
 * `visibleKeys`: keys de los ítems del menú lateral que el usuario ve.
 */
export const pendingAnnouncements = (
  profile: User,
  visibleKeys: Set<string>,
  now = Date.now()
): Announcement[] => {
  // Superadmins (nosotros) y document_helper tienen otro menú.
  if (
    profile.roles?.includes('superadmin') ||
    profile.roles?.includes('document_helper')
  )
    return [];
  const createdAt = Date.parse(profile.created_at);
  const seen = profile.seen_announcements ?? [];
  return ANNOUNCEMENTS.filter((announcement) => {
    const launchedAt = Date.parse(announcement.launchedAt);
    return (
      launchedAt <= now &&
      launchedAt > createdAt &&
      !seen.includes(announcement.id) &&
      announcement.requires.every((key) => visibleKeys.has(key))
    );
  });
};

export type AnnouncementStatus = 'seen' | 'pending' | 'not-applicable';

/**
 * Para el seguimiento del superadmin: si un usuario ya vio una novedad, si le
 * falta verla o si no le corresponde (sin acceso o creado después del
 * lanzamiento). Mira rol y permisos, no el menú de ese usuario.
 */
export const announcementStatus = (
  profile: User,
  id: string
): AnnouncementStatus => {
  const announcement = ANNOUNCEMENTS.find((a) => a.id === id);
  if (!announcement) return 'not-applicable';
  if (profile.seen_announcements?.includes(id)) return 'seen';
  const roles = profile.roles ?? [];
  const isAdmin = roles.includes('admin');
  if (!isAdmin && !roles.includes('basic')) return 'not-applicable';
  if (Date.parse(announcement.launchedAt) <= Date.parse(profile.created_at))
    return 'not-applicable';
  if (!isAdmin) {
    const enabled = new Set(
      (profile.permissions ?? [])
        .filter((permission) => permission.enabled)
        .map((permission) => permission.sidebarItemKey)
    );
    if (!announcement.requires.every((key) => enabled.has(key)))
      return 'not-applicable';
  }
  return 'pending';
};
